import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@progress/kendo-react-buttons';
import { Invitation } from 'services/api/invitations/types';
import { useNotificationsContext } from 'context';
import { ErrorNotification } from 'app/components/Notifications';
import { Success } from 'app/components/Notifications';
import { DeleteDialog } from 'app/components/dialogs';
import { invitationsApi } from 'services/api/invitations/queries';
import { useFormatter } from 'shared/formatters';

interface IPendingSentInvitationRow {
  isMobile: boolean;
  invitation: Invitation;
}

function PendingSentInvitationRow(props: IPendingSentInvitationRow) {
  const { t } = useTranslation();

  const { invitation } = props;
  const { addNotification, addSnackbar } = useNotificationsContext();
  const [resendInvitation, { isLoading: isResendInvitationLoading }] =
    invitationsApi.useResendInvitationMutation();
  const [revokeInvitation] = invitationsApi.useRevokeInvitationMutation();
  const [isRevokeAccessDialogOpen, setIsRevokeAccessDialogOpen] =
    useState(false);

  const isLoading = isResendInvitationLoading;

  const { formatDate, formatTime } = useFormatter();
  const invitedAt = formatDate(invitation.accessDetails.invitedAt);
  const startsAtTime = formatTime(invitation.accessDetails.startsAt);
  const startsAtDate = formatDate(invitation.accessDetails.startsAt);
  const endsAtTime = formatTime(invitation.accessDetails.endsAt);
  const endsAtDate = formatDate(invitation.accessDetails.endsAt);

  // TODO replace with a new hook, like usePrefixedTranslation(namespace: string, prefix: string)
  const translationKey =
    'access_sharing.pending_received_invitations.table.actions';

  const handleResentInvitation = async () => {
    const { accessDetails, sharedItemDetails } = invitation;

    try {
      await resendInvitation({
        shareType: sharedItemDetails.shareType,
        exposedId: [sharedItemDetails.exposedId],
        sharedWith: [
          {
            email: accessDetails.sharedWithEmail!,
            role: accessDetails.sharedRole,
            endsAt: accessDetails.endsAt,
            startsAt: accessDetails.startsAt,
          },
        ],
      });
      addNotification(
        <Success
          text={t('access_sharing.notifications.invitation-successfully-sent')}
        />,
      );
    } catch (error) {
      addNotification(<ErrorNotification text={t('server_error_message')} />);
      console.error(error);
    }
  };

  const handleRevokeAccess = async () => {
    try {
      await revokeInvitation([
        {
          shareType: invitation.sharedItemDetails.shareType,
          exposedAccessSharingRecordId: invitation.exposedAccessSharingRecordId,
        },
      ]).unwrap();

      addSnackbar(
        <div className="snackbar-container">
          <span>{t(`${translationKey}.revoke.success_snackbar`)}</span>
        </div>,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={t('server_error_message')} />);
    } finally {
      setIsRevokeAccessDialogOpen(false);
    }
  };

  return (
    <>
      <DeleteDialog
        open={isRevokeAccessDialogOpen}
        title={t(`${translationKey}.revoke.confirm_title`, {
          email: invitation.accessDetails.sharedWithEmail,
          machine: invitation.sharedItemDetails.sharedItemLabel,
        })}
        confirmationLabel={t(`${translationKey}.revoke.label`)}
        onClose={() => setIsRevokeAccessDialogOpen(false)}
        onConfirm={handleRevokeAccess}
      />

      {!props.isMobile ? (
        <div className="access-sharing-table-row pending-sent-invitation">
          <div className="equipment-table-field ellipsis">
            {invitation.accessDetails.sharedWithEmail}
          </div>
          <div className="equipment-table-field ellipsis">
            {invitation.sharedItemDetails.sharedItemLabel}
          </div>
          <div className="equipment-table-field">
            {invitation.accessDetails.sharedRole}
          </div>
          <div className="pending-details min-w-0">
            <span className="equipment-table-field ellipsis">
              {invitation.accessDetails.invitedByName}
            </span>
            <span className="grayed-text">{invitedAt}</span>
          </div>
          <div className="pending-details">
            <span>{startsAtDate}</span>
            <span>{startsAtTime}</span>
          </div>
          <div className="pending-details">
            <span>{endsAtDate}</span>
            <span>{endsAtTime}</span>
          </div>
          <div className="pending-invitation-buttons-container">
            <Button
              className="pending-invitation-button"
              disabled={isLoading}
              onClick={() => setIsRevokeAccessDialogOpen(true)}
            >
              {t(`${translationKey}.revoke.label`)}
            </Button>
            <Button
              className="pending-invitation-button"
              themeColor="tertiary"
              onClick={handleResentInvitation}
              disabled={isLoading}
            >
              {t(`${translationKey}.resend`)}
            </Button>
          </div>
        </div>
      ) : (
        <div className="pending-invitation-card">
          <div className="title-and-date sent-invitation">
            <div className="pending-invitation-title">
              {invitation.accessDetails.sharedRole}
            </div>
            <div className="pending-invitation-date">{invitedAt}</div>
          </div>
          <div className="pending-invitation-email">
            {invitation.accessDetails.sharedWithEmail}
          </div>
          <div className="access-to-container">
            <div className="bolded-info">{t('access_to')}</div>
            <div>{invitation.sharedItemDetails.sharedItemLabel}</div>
          </div>
          <div className="invited-by-container">
            <span className="bolded-info">{t('invited_by')}: </span>
            <span>{invitation.accessDetails.invitedByName}</span>
          </div>
          <div className="pending-invitation-buttons-container">
            <Button
              className="pending-invitation-button"
              disabled={isLoading}
              onClick={() => setIsRevokeAccessDialogOpen(true)}
            >
              {t(`${translationKey}.revoke.label`)}
            </Button>
            <Button
              className="pending-invitation-button"
              themeColor="tertiary"
              onClick={handleResentInvitation}
              disabled={isLoading}
            >
              {t(`${translationKey}.resend`)}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default PendingSentInvitationRow;
